import { sortBy, isEmpty, includes } from 'lodash';
import { getFilterDisplayName, getFilterIcon } from '../../utils/filterDisplayName';
import DimensionListTypeaheadPicker from '../TypeaheadPicker/DimensionListTypeaheadPicker';
import ActionTypeaheadPicker from '../TypeaheadPicker/ActionTypeaheadPicker';
import { searchByEndpoint } from '../../actions/search';
import { dimensionPropertyType, purchaseOrigin, customerTier } from '../../constants';
import Dropdown from '../Dropdown';
import FormikTypeaheadPickerWithOperator from '../TypeaheadPicker/FormikTypeaheadPickerWithOperator';
import TextFieldWithOperator from '../TextField/TextFieldWithOperator';

export const productTextTransformer = value => {
  return {
    property: 'product',
    propertyType: value.propertyType || 'value',
    value: value.id,
    text: value.text,
  };
};

export const purchaseOriginTextTransformer = (value, { property }) => {
  return {
    property,
    propertyType: value.propertyType || 'value',
    value,
    text: value,
  };
};

export const textTransformer = (value, { property }) => {
  return {
    property,
    propertyType: value.propertyType || 'value',
    value: value.id ?? value,
    text: value.text ?? value,
  };
};

export const defaultTransformer = (value, property) => {
  return {
    property,
    propertyType: 'value',
    value,
  };
};

const idTransformer = (value, { property }) => {
  return {
    property,
    propertyType: value.propertyType || 'value', // want 'value' if not explicitly set
    value: value.id,
    operator: value.operator,
  };
};

const getFilterValue = dimension => {
  if (dimension.propertyType === dimensionPropertyType.operator) {
    return `default.${dimension.value.key}`;
  }
  return `default.${dimension.value}`;
};

export const defaultPivotWiderTransformer = dimension => ({
  id: dimension.value,
  key: getFilterValue(dimension),
  text: getFilterDisplayName(dimension),
  icon: getFilterIcon(dimension),
  ...dimension,
});

export const productTypeColumn = {
  key: 'productTypeId',
  text: 'Product Type',
  name: 'Product Type',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: DimensionListTypeaheadPicker,
    initialOptions: [],
    listType: 'productTypeId',
    searchType: 'product-types',
    placeholder: 'Any',
    searchParamName: 'productTypeDescription',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  property: 'productTypeId',
  isResizable: true,
  transformer: idTransformer,
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const activityTypeColumn = {
  key: 'activityType',
  text: 'Activity Type',
  name: 'Activity Type',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: DimensionListTypeaheadPicker,
    initialOptions: [],
    listType: 'activityType',
    searchType: 'activity-types',
    searchParamName: 'activityTypeDescription',
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  property: 'activityType',
  isResizable: true,
  transformer: idTransformer,
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const saleTypeColumn = {
  key: 'saleType',
  text: 'Sale Type',
  name: 'Sale Type',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: DimensionListTypeaheadPicker,
    initialOptions: [],
    listType: 'saleType',
    searchType: 'sale-types',
    searchParamName: 'saleTypeDescription',
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  property: 'saleType',
  isResizable: true,
  transformer: idTransformer,
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const channelColumn = {
  key: 'channel',
  text: 'Channel',
  name: 'Channel',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: DimensionListTypeaheadPicker,
    initialOptions: [],
    listType: 'channel',
    searchType: 'channels',
    searchParamName: 'channelTypeName',
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  property: 'channel',
  isResizable: true,
  transformer: idTransformer,
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const currencyColumn = {
  key: 'currency',
  text: 'Currency',
  name: 'Currency',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: DimensionListTypeaheadPicker,
    initialOptions: [],
    listType: 'currency',
    searchType: 'currencies',
    searchParamName: 'currencyName',
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  property: 'currency',
  isResizable: true,
  transformer: idTransformer,
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const geographyColumn = {
  key: 'geography',
  property: 'geography',
  text: 'Geography',
  name: 'Geography',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: DimensionListTypeaheadPicker,
    initialOptions: [],
    listType: 'geography',
    searchType: 'geographies',
    searchParamName: 'countryName',
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: idTransformer,
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};
export const dataCenterColumn = {
  key: 'dataCenter',
  property: 'dataCenter',
  text: 'Data Center',
  name: 'Data Center',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: DimensionListTypeaheadPicker,
    initialOptions: [],
    listType: 'dataCenter',
    searchType: 'dataCenters',
    searchParamName: 'azureDatacenters',
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: idTransformer,
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const subscriptionGuidColumn = {
  key: 'subscriptionGuid',
  property: 'subscriptionGuid',
  text: 'Subscription Guid',
  name: 'Subscription Guid',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: ActionTypeaheadPicker,
    onTypeAction: async (query, dispatch) => {
      const dlSearchResult = await dispatch(
        searchByEndpoint('dimensionLists', { listName: query, listType: 'subscriptionGuid' }),
      );

      let error;

      if (dlSearchResult.error) {
        error = dlSearchResult.error;
      }

      // error state does not mean that we shouldn't provide what results we do receive
      const subscriptionGuidLists = dlSearchResult.error ? [] : dlSearchResult.payload;
      return {
        items: sortBy(
          subscriptionGuidLists?.map(guid => {
            return {
              icon: 'BulletedList',
              id: guid.listId,
              key: `subscriptionGuid.${guid.listName}`,
              propertyType: 'dimensionList',
              text: guid.listName,
              header: 'SubscriptionGuid',
              guid,
            };
          }),
          p => p.text,
        ),
        error,
      };
    },
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: value => {
    return {
      property: 'subscriptionGuid',
      propertyType: value.propertyType,
      value: value.id,
      operator: value.operator,
    };
  },
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};
export const resourceGuidColumn = {
  key: 'resourceGuid',
  property: 'resourceGuid',
  text: 'Resource Guid',
  name: 'Resource Guid',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: ActionTypeaheadPicker,
    onTypeAction: async (query, dispatch) => {
      const dlSearchResult = await dispatch(
        searchByEndpoint('dimensionLists', { listName: query, listType: 'resourceGuid' }),
      );

      let error;

      if (dlSearchResult.error) {
        error = dlSearchResult.error;
      }
      const resourceGuidLists = dlSearchResult.error ? [] : dlSearchResult.payload;
      return {
        items: sortBy(
          resourceGuidLists?.map(guid => {
            return {
              icon: 'BulletedList',
              id: guid.listId,
              key: `resourceGuid.${guid.listName}`,
              propertyType: 'dimensionList',
              text: guid.listName,
              header: 'ResourceGuid',
              guid,
            };
          }),
          p => p.text,
        ),
        error,
      };
    },
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: value => {
    return {
      property: 'resourceGuid',
      propertyType: value.propertyType,
      value: value.id,
      operator: value.operator,
    };
  },
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};
export const billingTypeNameColumn = {
  key: 'billingTypeName',
  property: 'billingTypeName',
  text: 'Billing Type Name',
  name: 'Billing Type Name',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: ActionTypeaheadPicker,
    onTypeAction: async (query, dispatch) => {
      const dlSearchResult = await dispatch(
        searchByEndpoint('dimensionLists', { listName: query, listType: 'billingTypeName' }),
      );

      let error;

      if (dlSearchResult.error) {
        error = dlSearchResult.error;
      }

      // error state does not mean that we shouldn't provide what results we do receive
      const billingTypeNameLists = dlSearchResult.error ? [] : dlSearchResult.payload;
      return {
        items: sortBy(
          billingTypeNameLists?.map(guid => {
            return {
              icon: 'BulletedList',
              id: guid.listId,
              key: `billingTypeName.${guid.listName}`,
              propertyType: 'dimensionList',
              text: guid.listName,
              header: 'BillingTypeName',
              guid,
            };
          }),
          p => p.text,
        ),
        error,
      };
    },
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: value => {
    return {
      property: 'billingTypeName',
      propertyType: value.propertyType,
      value: value.id,
      operator: value.operator,
    };
  },
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};
export const productColumn = {
  key: 'product',
  text: 'Product',
  name: 'Product',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: ActionTypeaheadPicker,
    onTypeAction: async (query, dispatch) => {
      const productsSearchResult = await dispatch(
        searchByEndpoint('products', { productName: query }),
      );
      const productListSearchResult = await dispatch(
        searchByEndpoint('dimensionLists', { listName: query, listType: 'product' }),
      );

      let error;

      if (productsSearchResult.error) {
        error = productListSearchResult;
      }
      if (productListSearchResult.error) {
        error += `\n${productListSearchResult.error}`;
      }

      // error state does not mean that we shouldn't provide what results we do receive
      const products = productsSearchResult.error ? [] : productsSearchResult.payload;
      const productLists = productListSearchResult.error ? [] : productListSearchResult.payload;
      return {
        items: [] // safe concat
          .concat(
            sortBy(
              products?.map(product => ({
                icon: 'Product',
                id: product._id,
                key: `product.${product._id}`,
                propertyType: 'value',
                text: product.name,
                header: 'Products',
                product,
              })),
              p => p.text,
            ),
          )
          .concat(
            sortBy(
              productLists?.map(productList => ({
                icon: 'BulletedList',
                id: productList.listId,
                key: `list.${productList.listId}`,
                propertyType: 'dimensionList',
                text: productList.listName,
                header: 'Lists',
                productList,
              })),
              p => p.text,
            ),
          ),
        error,
      };
    },
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
    headerOrder: ['Products', 'Lists'],
  },
  minWidth: 250,
  isResizable: true,
  transformer: value => {
    return {
      property: 'product',
      propertyType: value.propertyType,
      value: value.id,
      operator: value.operator,
    };
  },
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const offerIdColumn = {
  key: 'offerId',
  property: 'offerId',
  text: 'Offer Id',
  name: 'OfferId',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: ActionTypeaheadPicker,
    onTypeAction: async (query, dispatch) => {
      const dlSearchResult = await dispatch(
        searchByEndpoint('dimensionLists', { listName: query, listType: 'offerId' }),
      );

      let error;

      if (dlSearchResult.error) {
        error = dlSearchResult.error;
      }

      // error state does not mean that we shouldn't provide what results we do receive
      const offerIdList = dlSearchResult.error ? [] : dlSearchResult.payload;
      return {
        items: sortBy(
          offerIdList?.map(offerId => {
            return {
              icon: 'BulletedList',
              id: offerId.listId,
              key: `offerId.${offerId.listName}`,
              propertyType: 'dimensionList',
              text: offerId.listName,
              header: 'Offers',
              offerId,
            };
          }),
          p => p.text,
        ),
        error,
      };
    },
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: value => {
    return {
      property: 'offerId',
      propertyType: value.propertyType,
      value: value.id,
      operator: value.operator,
    };
  },
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const tpIdColumn = {
  key: 'tpId',
  property: 'tpId',
  text: 'TPID',
  name: 'TPID',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: ActionTypeaheadPicker,
    onTypeAction: async (query, dispatch) => {
      const dlSearchResult = await dispatch(
        searchByEndpoint('dimensionLists', { listName: query, listType: 'tpId' }),
      );

      let error;

      if (dlSearchResult.error) {
        error = dlSearchResult.error;
      }

      // error state does not mean that we shouldn't provide what results we do receive
      const tpIdList = dlSearchResult.error ? [] : dlSearchResult.payload;
      return {
        items: sortBy(
          tpIdList?.map(data => {
            return {
              icon: 'BulletedList',
              id: data.listId,
              key: `tpId.${data.listName}`,
              propertyType: 'dimensionList',
              text: data.listName,
              header: 'TPID',
              data,
            };
          }),
          p => p.text,
        ),
        error,
      };
    },
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: value => {
    return {
      property: 'tpId',
      propertyType: value.propertyType,
      value: value.id,
      operator: value.operator,
    };
  },
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const customerIdColumn = {
  key: 'customerId',
  property: 'customerId',
  text: 'Customer Id',
  name: 'CustomerId',
  component: FormikTypeaheadPickerWithOperator,
  props: {
    Typeahead: ActionTypeaheadPicker,
    onTypeAction: async (query, dispatch) => {
      const dlSearchResult = await dispatch(
        searchByEndpoint('dimensionLists', { listName: query, listType: 'customerId' }),
      );

      let error;

      if (dlSearchResult.error) {
        error = dlSearchResult.error;
      }

      // error state does not mean that we shouldn't provide what results we do receive
      const customerIdList = dlSearchResult.error ? [] : dlSearchResult.payload;
      return {
        items: sortBy(
          customerIdList?.map(data => {
            return {
              icon: 'BulletedList',
              id: data.listId,
              key: `customerId.${data.listName}`,
              propertyType: 'dimensionList',
              text: data.listName,
              header: 'CustomerId',
              data,
            };
          }),
          p => p.text,
        ),
        error,
      };
    },
    placeholder: 'Any',
    includePlaceholderOption: true,
    displayIconInTextBox: true,
  },
  minWidth: 250,
  isResizable: true,
  transformer: value => {
    return {
      property: 'customerId',
      propertyType: value.propertyType,
      value: value.id,
      operator: value.operator,
    };
  },
  styles: { gripperBarVerticalStyle: { display: 'none !important' } },
};

export const meterColumn = {
  key: 'meterName',
  property: 'meterName',
  text: 'Meter Name',
  name: 'Meter Name',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const productNameColumn = {
  key: 'productName',
  property: 'productName',
  text: 'Product Name',
  name: 'Product Name',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const productIdColumn = {
  key: 'productId',
  property: 'productId',
  text: 'Product Id',
  name: 'Product Id',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const imageTypeColumn = {
  key: 'imageType',
  property: 'imageType',
  text: 'Image Type',
  name: 'Image Type',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const skuNameColumn = {
  key: 'skuName',
  property: 'skuName',
  text: 'SKU Name',
  name: 'SKU Name',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const coreSizeColumn = {
  key: 'coreSize',
  property: 'coreSize',
  text: 'Core Size',
  name: 'Core Size',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const productTierColumn = {
  key: 'productTier',
  property: 'productTier',
  text: 'Product Category',
  name: 'Product Category',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const productGuidColumn = {
  key: 'productGuid',
  property: 'productGuid',
  text: 'Product Guid',
  name: 'Product Guid',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};
export const serviceIdColumn = {
  key: 'serviceId',
  property: 'serviceId',
  text: 'Service Id',
  name: 'Service Id',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};
export const usageAllocationTypeColumn = {
  key: 'usageAllocationType',
  property: 'usageAllocationType',
  text: 'Usage Allocation Type',
  name: 'Usage Allocation Type',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const transactiontTypeColumn = {
  key: 'transactionType',
  property: 'transactionType',
  text: 'Azure Transaction Type',
  name: 'Azure Transaction Type',
  component: Dropdown,
  props: {
    options: [
      { key: 'Consumption', text: 'Consumption' },
      { key: 'ConsumptionDaily', text: 'Consumption Daily' },
      { key: 'ELS', text: 'ELS' },
      { key: 'ReservedInstance', text: 'Reserved Instance' },
      { key: 'Revenue', text: 'Revenue' },
      { key: 'AzureSpend', text: 'Azure Spend' },
    ],
  },
  pivotWiderTransformer: dim => dim.value,
  transformer: (value, { property }) => {
    return {
      property,
      propertyType: 'value',
      value,
    };
  },
  styles: {
    gripperBarVerticalStyle: { display: 'none !important' },
  },
};

export const RIStateColumn = {
  key: 'state',
  property: 'state',
  text: 'RI State',
  name: 'RI State',
  component: Dropdown,
  props: {
    options: [
      { key: 'Cancelled', text: 'Cancelled' },
      { key: 'Created', text: 'Created' },
      { key: 'Expired', text: 'Expired' },
      { key: 'Split', text: 'Split' },
    ],
  },
  pivotWiderTransformer: dim => dim.value,
  transformer: (value, { property }) => {
    return {
      property,
      propertyType: 'value',
      value,
    };
  },
  styles: {
    gripperBarVerticalStyle: { display: 'none !important' },
  },
};

export const purchasePriceColumn = {
  key: 'purchasePrice',
  property: 'purchasePrice',
  text: 'Purchase Price',
  name: 'Purchase Price',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
    type: 'number',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const purchaseOriginColumn = {
  key: 'purchaseOrigin',
  property: 'purchaseOrigin',
  text: 'Purchase Origin',
  name: 'Purchase Origin',
  minWidth: 250,
  isResizable: true,
  props: {
    options: purchaseOrigin,
  },
  component: Dropdown,
  pivotWiderTransformer: dim => dim.value,
  transformer: (value, { property }) => {
    return {
      property,
      propertyType: 'value',
      value,
    };
  },
  styles: {
    gripperBarVerticalStyle: { display: 'none !important' },
  },
};

export const customerTierColumn = {
  key: 'customerTier',
  property: 'customerTier',
  text: 'Customer Tier',
  name: 'Customer Tier',
  minWidth: 200,
  isResizable: true,
  props: {
    options: customerTier,
  },
  component: Dropdown,
  pivotWiderTransformer: dim => dim.value,
  transformer: (value, { property }) => {
    return {
      property,
      propertyType: 'value',
      value,
    };
  },
  styles: {
    gripperBarVerticalStyle: { display: 'none !important' },
  },
};

export const termColumn = {
  key: 'term',
  property: 'term',
  text: 'Term',
  name: 'Term',
  component: TextFieldWithOperator,
  props: {
    placeholder: 'Any',
  },
  // plain text fields just need the dimension's value
  pivotWiderTransformer: dim => dim.value,
};

export const unitOfMeasureColumn = {
  key: 'unitOfMeasure',
  property: 'unitOfMeasure',
  text: 'Unit of Measure',
  name: 'Unit of Measure',
  minWidth: 200,
  isResizable: true,
  props: {
    options: [
      { key: 'B', text: 'Bytes' },
      { key: 'KB', text: 'Kilobytes' },
      { key: 'MB', text: 'Megabytes' },
      { key: 'GB', text: 'Gigabytes' },
      { key: 'TB', text: 'Terabytes' },
    ],
  },
  component: Dropdown,
  pivotWiderTransformer: dim => dim.value,
  transformer: (value, { property }) => {
    return {
      property,
      propertyType: 'value',
      value,
    };
  },
  styles: {
    gripperBarVerticalStyle: { display: 'none !important' },
  },
};

export const getAllColumnsWithOperators = (showHideOperator = false, columnName = []) => {
  const dimensionColumns = [
    RIStateColumn,
    productColumn,
    geographyColumn,
    dataCenterColumn,
    currencyColumn,
    channelColumn,
    productTypeColumn,
    activityTypeColumn,
    saleTypeColumn,
    meterColumn,
    customerTierColumn,
    termColumn,
    purchaseOriginColumn,
    unitOfMeasureColumn,
    offerIdColumn,
    productGuidColumn,
    serviceIdColumn,
    usageAllocationTypeColumn,
    subscriptionGuidColumn,
    resourceGuidColumn,
    billingTypeNameColumn,
    transactiontTypeColumn,
    purchasePriceColumn,
    tpIdColumn,
    customerIdColumn,
    productNameColumn,
    productIdColumn,
    imageTypeColumn,
    skuNameColumn,
    coreSizeColumn,
    productTierColumn,
  ].map(column => {
    if (showHideOperator) {
      const showOperator = isEmpty(columnName)
        ? showHideOperator
        : includes(columnName, column.key);
      let updatedColumn = {
        ...column,
        minWidth: 250,
        props: { ...column.props, showOperator },
      };

      if (column.component === TextFieldWithOperator) {
        updatedColumn = {
          ...updatedColumn,
          transformer: value => {
            return {
              property: column.key,
              propertyType: 'value',
              value: value.val,
              operator: value.operator,
            };
          },
          pivotWiderTransformer: dim => ({
            val: dim.value,
            operator: dim.operator,
          }),
        };
      }
      return updatedColumn;
    }
    return column;
  });

  return dimensionColumns;
};

export const allColumns = getAllColumnsWithOperators();

export default allColumns;
